import React from "react";
import { Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { rangeAfterFromChange, rangeAfterToChange } from "../../utils/dateRange";

const pickerSx = { width: "180px", backgroundColor: "#fff", borderRadius: "4px" };
const toValue = (date) => (date ? dayjs(date) : null);
const toText = (date) => (date ? date.format("YYYY-MM-DD") : "");

/**
 * "From" and "To" pickers of the Budget tab's Custom Date filter.
 *
 * The To date can never be earlier than From:
 *  - days before From are greyed out in the To calendar (`minDate`);
 *  - a typed-in To before From is ignored;
 *  - picking a From after the current To clears To, so the user chooses it again.
 *
 * `onChange({ from, to })` receives "YYYY-MM-DD" strings ("" when unset) and is only
 * ever called with a valid range.
 */
const CustomDateRangePicker = ({ from, to, onChange }) => {
  const handleFromChange = (newValue) => {
    onChange(rangeAfterFromChange({ from, to }, toText(newValue)));
  };

  const handleToChange = (newValue) => {
    const range = rangeAfterToChange({ from, to }, toText(newValue));
    if (range) onChange(range);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="d-flex align-items-center gap-2">
        <Typography sx={{ color: "#969696", fontWeight: 500 }}>From</Typography>
        <DatePicker
          value={toValue(from)}
          onChange={handleFromChange}
          slotProps={{ textField: { size: "small", inputProps: { "aria-label": "From date" } } }}
          sx={pickerSx}
        />
      </div>
      <div className="d-flex align-items-center gap-2">
        <Typography sx={{ color: "#969696", fontWeight: 500 }}>To</Typography>
        <DatePicker
          value={toValue(to)}
          minDate={from ? dayjs(from) : undefined}
          onChange={handleToChange}
          slotProps={{ textField: { size: "small", inputProps: { "aria-label": "To date" } } }}
          sx={pickerSx}
        />
      </div>
    </LocalizationProvider>
  );
};

export default CustomDateRangePicker;
